import { watchImpressions } from './watchImpressions';

/**
 * Take a string like 'impressionExtraSomeProperty'
 * and turn it into 'someProperty'
 * @param {string} str
 * @return {string}
 */
const removePrefix = (str: string) => {
  str = str.substring('impressionExtra'.length);
  return str[0].toLowerCase() + str.slice(1);
};

/**
 * Watches impressions for any elements
 * that are present on pageload with `data-impression-entity-id`s.
 * These usually come from slim templates.
 * If you are working in React code, you probably want `ImpressionWatcher`.
 *
 * The data it grabs is:
 * ```
 * data-impression-entity-id=""
 * data-impression-entity-type=""
 * data-impression-source=""
 * data-impression-extra-more-data=""
 * data-impression-extra-another-property=""
 * ```
 *
 * Previously, this function used `$a.each_current_and_future`
 * to watch impressions on dynamically added elements,
 * but it had negative performance implications.
 * Since this is only used for slim templates,
 * which are rendered as HTML, it only needs to run once.
 *
 * If you need to watch impressions on a dynamically added element
 * and you're not in React, I recommend using `watchImpressions` directly.
 * @param {number} threshold From 0 - 1, what percentage of an element must show
 * before sending the impression
 */
export function watchDomImpressions(threshold?: number) {
  document.addEventListener('DOMContentLoaded', () => {
    const impressionables = document.querySelectorAll<HTMLElement>(
      '*[data-impression-entity-id]',
    );

    Array.from(impressionables).forEach((el) => {
      const entity_id = el.dataset.impressionEntityId as Required<string>;
      const entity_type = Number(
        el.dataset.impressionEntityType,
      ) as Required<number>;
      const source = el.dataset.impressionSource as Required<string>;
      const extra = Object.fromEntries(
        Object.entries(el.dataset)
          .filter(([key]) => key.startsWith('impressionExtra'))
          .map(([key, value]) => [removePrefix(key), value]),
      );

      watchImpressions(el, {
        entity_id,
        entity_type,
        extra,
        source,
        threshold,
      });
    });
  });
}
